import React, { forwardRef, useContext } from 'react';
import { useButton, UseButtonOptions } from '../../hooks/useButton';
import { BoxStyleSpaceScale } from '../../legacy/Box';
import { Flex } from '../../legacy/Flex';
import { Pane } from '../../legacy/Pane';
import { SpaceScale } from '../../styled-system/types/scale';
import { identifyComponent } from '../../util';
import { ListContext } from '../List/context';
import { ListItemBaseProps } from '../ListItem';
import css from './index.module.scss';

export interface MenuItemProps extends ListItemBaseProps {
  /**
   * If `true`, the list item will be disabled.
   */
  disabled?: boolean;

  /**
   * Use to apply selected styling.
   */
  highlighted?: boolean;

  /**
   * Override the tabIndex.
   */
  tabIndex?: UseButtonOptions['tabIndex'];

  /**
   * If `true`, vertical padding is removed from the list.
   */
  disablePadding?: boolean;
}

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(function MenuItem(
  {
    testId = 'menu-item',
    children,
    disabled = false,
    highlighted = false,
    compact: compactProp,
    disableGutters: disableGuttersProp,
    disablePadding = false,
    role = 'menuitem',
    onClick,
    tabIndex,
    ...rest
  },
  ref,
) {
  const listContext = useContext(ListContext);
  const compact = compactProp ?? listContext.compact;
  const disableGutters = disableGuttersProp ?? listContext.disableGutters;
  const verticalPadding: SpaceScale = disablePadding ? 0 : compact ? 4 : 8;
  const horizontalPadding: SpaceScale = disableGutters ? 0 : 12;
  const padding: BoxStyleSpaceScale = [verticalPadding, horizontalPadding];

  const { getRootProps, getClassNames } = useButton('li', { disabled, role, tabIndex });

  return (
    <li
      data-component="MenuItem"
      data-testid={testId}
      ref={ref}
      {...rest}
      {...getRootProps({ onClick })}
      {...identifyComponent('menu-item', rest)}
      className={getClassNames(
        {
          disabled: css.disabled,
        },
        css.menuItem,
      )}
      role={role}
    >
      <Pane padding={padding} backgroundColor={!disabled && highlighted ? 'hover' : 'default'}>
        <Flex justifyContent="flex-start" alignItems="center" gap={12}>
          {children}
        </Flex>
      </Pane>
    </li>
  );
});
